import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X, AlertTriangle, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

interface RejectModalProps {
  productName: string;
  currentStep: string;
  previousSteps: string[];
  onClose: () => void;
  onReject: (targetStep: string, reason: string) => Promise<void>;
}

const STEP_NAMES: Record<string, string> = {
  'draft': 'Üretim (Taslak)',
  'production_entry': 'Üretim (SKU Bekliyor)',
  'nebim_entry': 'Nebim (İşlemde)',
  'barcode_sent': 'Üretim (Üreticiye Gönderim)',
  'stock_entry': 'Depo (Stok Girişi)',
  'api_transfer': 'Yönetim (Ticimaxa Aktarım)',
  'content_entry': 'İçerik (Açıklama)',
  'category_setup': 'İçerik (Kategorileme)',
  'image_upload': 'Görsel (Yükleme)',
  'publish_pending': 'Yönetim (Yayın Bekliyor)',
  'published': 'Yönetim (Yayında)'
};

const RejectModal: React.FC<RejectModalProps> = ({
  productName,
  currentStep,
  previousSteps,
  onClose,
  onReject
}) => {
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [targetStep, setTargetStep] = useState(previousSteps.length > 0 ? previousSteps[previousSteps.length - 1] : '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!targetStep) {
      toast.error('Geri yollanacak adım belirlenemedi');
      return;
    }
    
    if (!rejectReason.trim()) {
      toast.error('Lütfen geri yollama nedenini belirtin');
      return;
    }
    
    try {
      setLoading(true);
      await onReject(targetStep, rejectReason.trim());
      toast.success('Ürün başarıyla geri yollandı');
      onClose();
    } catch (error) {
      toast.error('İşlem başarısız oldu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 border-b">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Ürünü Geri Yolla</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">{productName}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Mevcut Adım */}
            <div className="bg-muted/30 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Mevcut Adım</p>
              <p className="font-medium">{STEP_NAMES[currentStep] || currentStep}</p>
            </div>

            {/* Geri Yollanacak Adım - Dropdown */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Geri Yollanacak Adım *
              </label>
              <div className="relative">
                <ArrowLeft className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <select
                  value={targetStep}
                  onChange={(e) => setTargetStep(e.target.value)}
                  required
                  className="w-full pl-10 pr-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring bg-white"
                >
                  <option value="">Departman Seçin</option>
                  {previousSteps.map((step) => (
                    <option key={step} value={step}>
                      {STEP_NAMES[step] || step}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-muted-foreground">
                Ürün seçilen departmana geri gönderilecektir.
              </p>
            </div>

            {/* Geri Yollama Nedeni */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Geri Yollama Nedeni *
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                required
                rows={4}
                placeholder="Eksik veya hatalı olan bilgileri detaylı olarak açıklayın..."
                className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Bu not, ilgili ekibe iletilecektir.
              </p>
            </div>

            {/* Uyarı Mesajı */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Dikkat!</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Ürün seçilen adıma geri yollanacak ve o adımdan itibaren tekrar işleme alınması gerekecek.
                  </p>
                </div>
              </div>
            </div>

            {/* Butonlar */}
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="flex-1"
              >
                İptal
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={loading || !targetStep || !rejectReason.trim()}
                className="flex-1"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Geri Yolla
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RejectModal;
